import { InfoIcon } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../../components/ui/tooltip";
import { Input } from "../../../../../components/ui/input";
import { useState } from "react";
import { useBookingGuestsCountMutation } from "../../../../../lib/bookingGuestsCountEditMutation";
import { Booking } from "../../../model";
import { calculateDaysStarted } from "../../../../Offers/EditOffer/ChangeYachtDetailsModal/ChangeYachtDetailsModal";
import { toast } from "../../../../../components/ui/use-toast";

export const EditGuestsNumberModal = ({
  open,
  onOpenChange,
  booking,
  guestsNo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: Booking;
  guestsNo: number;
}) => {
  const [guests, setGuests] = useState<number>(guestsNo);
  const updateGuests = useBookingGuestsCountMutation(booking.id);
  const matchingAmenity = (id: string) => booking.offerYacht?.yacht?.amenities?.find((am) => am.id === id);
  const onSubmit = () => {
    const startDate = booking.offerYacht?.startDate;
    const endDate = booking.offerYacht?.endDate;
    let days = 1;
    if (startDate && endDate) {
      days = calculateDaysStarted(startDate, endDate);
    }
    const location = booking.offerYacht?.startLocation?.names;
    const pickupExtras = booking.pricing?.pickupExtras;
    const pickupObligatory = booking.pricing?.pickupObligatory;
    const amenitiesDetails = booking.amenitiesDetails;
    const updatedAmenites: { text: string; price: number }[] = [];

    const updatedAmenitiesDetails = amenitiesDetails?.map((am) => {
      // return untouched amenity if the price is 0 or its paid in advance
      if (!am.paidLocally || !am.price) {
        return am;
      } else {
        const matchedAmenity = matchingAmenity(am.id);
        // return untouched amenity if the formula isnt available
        if (!matchedAmenity?.formula) {
          return am;
        } else {
          // recalculate the price based on the formula
          const updatedPrice = calculateAmenityPrice(
            location!,
            days,
            am.price,
            am.price,
            guests,
            matchedAmenity.formula,
          );
          // check if prices are different and update if needed
          if (updatedPrice !== am.price) {
            updatedAmenites.push({
              text: matchedAmenity.text,
              price: updatedPrice,
            });
            return {
              ...am,
              price: updatedPrice,
            };
          } else {
            return am;
          }
        }
      }
    });
    // update pickup amenites from pricing accordingly
    updatedAmenites?.forEach((am) => {
      pickupExtras?.forEach((value, index) => {
        if (am.text === value.text) {
          return (pickupExtras[index] = {
            ...value,
            price: am.price,
          });
        } else {
          return value;
        }
      });
      pickupObligatory?.forEach((value, index) => {
        if (am.text === value.text) {
          return (pickupObligatory[index] = {
            ...value,
            price: am.price,
          });
        } else {
          return value;
        }
      });
    });
    const toastId = toast({
      description: "Updating customer email...",
      variant: "default",
      duration: Infinity,
    }).id;

    updateGuests
      .mutateAsync({
        body: {
          amenitiesDetails: updatedAmenitiesDetails,
          pricing: {
            ...booking.pricing,
            pickupExtras,
            pickupObligatory,
          },
          guests,
        },
        toastId,
      })
      .then(() => onOpenChange(false));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[400px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-4">
            Edit guests number{" "}
            <Tooltip defaultOpen={false}>
              <TooltipTrigger className="max-w-max">
                <InfoIcon />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px] !top-[90px]">
                <p>Changing the number of guests will recalculate all of the local extras within the booking</p>
              </TooltipContent>
            </Tooltip>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 mt-6">
          <label htmlFor="guests">Number of guests</label>
          <Input name="guests" type="number" value={guests} onChange={(e) => setGuests(+e.currentTarget.value)} />
        </div>
        <DialogFooter>
          <Button onClick={() => onSubmit()} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export function calculateAmenityPrice(
  location: string,
  duration: number,
  clientPrice: number,
  listPrice: number,
  people: number,
  formula: string,
) {
  const fn = eval(`(location, duration, people, clientPrice, listPrice, varies) => {\n\n${formula}\n\n};`);
  const res = fn(location, duration, people, clientPrice, listPrice, 1);
  return res || 0;
}
